/**
 * CollisionTriageCard — referral name mismatches, surfaced on Today (review §C14).
 *
 * WHY THIS IS ON THE WORKLIST AND NOT ONLY IN SETTINGS. A collision is a phone
 * call to a referring practice, and it is the only referral state where a human
 * decision is the whole mechanism: no contact was created, no PDF was built, no
 * consent row exists, and the patient's escrowed details are on a 14-day fuse.
 * The patient, meanwhile, has already been told *"someone from our team will
 * call to check one detail."* Before this card, the only count lived inside the
 * panel it was counting — three clicks into CRM Settings, visible only to
 * someone who had already gone looking — and the only proactive channel was a
 * server-side log alert that pages ops, not the recruiter who makes the call.
 *
 * ADMIN-ONLY, BY THE API's CHOICE NOT OURS. Every `/portal-admin/referral*`
 * route is behind `requireCrmAdmin`, and the queue itself cannot be opened
 * without it. So this renders nothing for a non-admin recruiter rather than
 * showing them an error for a queue they cannot open. That is a real gap in
 * §C14's intent — see the review notes — but widening who may read referral
 * names is not a UI decision.
 *
 * IT FETCHES NOTHING. Names are deliberately not shown here — a count and an age
 * are enough to make someone open the queue, and a worklist that lists patient
 * names for a queue most people will never action is a PHI surface bought for
 * nothing. The first version of this card obeyed that on screen and broke it on
 * the wire: it ran its own 60s interval against the staff LIST endpoint, whose
 * projection carries first names, typed names and the condition hint, purely to
 * derive an integer — and the nav badge was polling the same rows on its own
 * timer alongside it. The count now comes from the shell's one fetch cycle
 * (useNavCounts → NavCountsContext), which asks a count-only endpoint and only
 * falls back to the name-carrying list where that route is not deployed yet.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { relTime, useIsCRMAdmin } from '../lib';
import { useSharedNavCounts } from '../useNavCounts';
import { COLLISION_SLA } from '../portalReferrals';

interface CollisionTriageCardProps {
  /** Reports the open-collision count on every load, so Today's "all caught up"
   *  card cannot render above a queue that still has someone waiting in it. */
  onCount?: (n: number) => void;
}

export default function CollisionTriageCard({ onCount }: CollisionTriageCardProps) {
  const isCrmAdmin = useIsCRMAdmin();
  // Null when the shell has not answered yet, when the fetch failed, or when the
  // viewer is not a CRM admin (the shell does not spend a poll on a guaranteed
  // 403). All three mean the same thing here: render nothing rather than assert
  // "0 waiting" from an answer we do not have.
  const { portalCollisions: summary } = useSharedNavCounts();

  // Reported in its own effect so an inline callback from the parent cannot turn
  // rendering into a loop (the FollowUpsDuePanel idiom).
  useEffect(() => { onCount?.(summary?.total ?? 0); }, [summary, onCount]);

  if (!isCrmAdmin || !summary || summary.total === 0) return null;

  const { total, late, oldestSince } = summary;
  const one = total === 1;

  return (
    <section
      aria-live="polite"
      className={`rounded-xl border bg-white shadow-sm dark:bg-gray-800 ${
        late > 0
          ? 'border-red-300 dark:border-red-800'
          : 'border-amber-300 dark:border-amber-800'
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <AlertTriangle
          className={`h-4 w-4 flex-shrink-0 ${
            late > 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
          }`}
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {total} referral{one ? '' : 's'} need{one ? 's' : ''} a call
          </h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            The patient's name did not match the one their provider referred, so nothing was created
            for {one ? 'it' : 'them'} — and {one ? 'they were' : 'each was'} told someone from our team
            would call.
            {oldestSince && ` Oldest waiting ${relTime(oldestSince)}.`}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {late > 0 && (
            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/40 dark:text-red-300">
              {late} over {COLLISION_SLA.lateHours}h
            </span>
          )}
          {/* Deep-links straight to the queue, pre-filtered. Landing on the
              Practices tab and asking a recruiter to find their way to the
              mismatches is how the count ends up ignored again. */}
          <Link
            to="/crm/settings/clinician-portal?tab=referrals&status=collision_review"
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            Review {one ? 'it' : 'them'}
          </Link>
        </div>
      </div>
    </section>
  );
}
